(() => {
  /* =========================================================
     PHOTOCARD BINDER — AJAX SELECT + HEADER COUNTERS + REMOVE ALL
     - Select toggles add/remove (stays on page)
     - Image click toggles selection too
     - Bottom bar shows: selected count + original price (struck) + final price + promo text
     - Checkout link is disabled when 0 cards selected
     - Drop closes countdown updates every minute
     - Total price updates live as cards are selected
     - Product group tabs: stock count + select all (if all in stock)
     - Promo text reads collection metafields via window.BINDER_PROMO
     - Stock polling swaps cards to claimed sleeves when they sell out
     ========================================================= */

  const SELECTOR = {
    tile:               ".binder-card[data-variant-id]",
    addBtn:             ".binder-add[data-variant-id]",

    tray:               "#binderTray",
    trayToggle:         "[data-tray-toggle]",
    trayBody:           "[data-tray-body]",
    trayList:           "[data-tray-list]",
    trayCount:          "[data-tray-count]",
    traySubtotal:       "[data-tray-subtotal]",

    counterSelected:    "[data-binder-selected]",
    counterTotalPrice:  "[data-binder-total-price]",
    counterOrigPrice:   "[data-binder-original-price]",
    counterCloses:      "[data-binder-closes]",
    promoText:          "[data-binder-promo]",

    clearAllBtn:        "[data-binder-clearall]",
    checkoutLink:       ".binder-checkout",
    bottomBar:          "[data-binder-bottom-bar]",

    groupTab:           ".binder-group",
    groupRemaining:     "[data-group-remaining]",
    groupTotal:         "[data-group-total]",
    groupPrice:         "[data-group-price]",
    groupSelectAll:     "[data-group-selectall]",
    groupStatus:        "[data-group-status]"
  };

  const state = {
    cart:     null,
    syncing:  false,
    toggling: false
  };

  const stockState  = {};
  const claimTimes  = {};

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ──────────────────────────────────────────────────────────
     HELPERS
     ────────────────────────────────────────────────────────── */

  function moneyFromCartJS(priceInPence) {
    return `£${(Number(priceInPence || 0) / 100).toFixed(2)}`;
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll("&",  "&amp;")
      .replaceAll("<",  "&lt;")
      .replaceAll(">",  "&gt;")
      .replaceAll('"',  "&quot;")
      .replaceAll("'",  "&#039;");
  }

  function getPromo() {
    return window.BINDER_PROMO || {};
  }

  function normaliseMoneyToPence(value) {
    if (value == null || value === "") return 0;
    const n = Number(value);
    if (Number.isNaN(n)) return 0;
    return Math.round(n * 100);
  }

  /* ──────────────────────────────────────────────────────────
     CART API
     ────────────────────────────────────────────────────────── */

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  }

  async function addToCart(variantId) {
    const res = await fetch("/cart/add.js", {
      method:  "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    });
    if (!res.ok) throw new Error("Add to cart failed");
    return res.json();
  }

  async function apiUpdate(updates) {
    const res = await fetch("/cart/update.js", {
      method:  "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ updates })
    });
    if (!res.ok) throw new Error("Update cart failed");
    return res.json();
  }

  function isVariantInCart(variantId) {
    if (!state.cart?.items) return false;
    return state.cart.items.some(
      (item) => String(item.variant_id) === String(variantId) && item.quantity > 0
    );
  }

  function dispatchCartRefreshEvents() {
    const detail = { cart: state.cart, source: "binder" };
    [
      "cart:refresh", "cart:updated", "cart:update",
      "theme:cart:refresh", "theme:cart:updated", "ajaxCart:refresh"
    ].forEach((name) => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      window.dispatchEvent(new CustomEvent(name, { detail }));
    });
  }

  /* ──────────────────────────────────────────────────────────
     SELECTED ITEM HELPERS
     ────────────────────────────────────────────────────────── */

  function getSelectedBinderItems() {
    if (!state.cart?.items) return [];

    const selectedIds = new Set();

    $$(SELECTOR.tile).forEach((tile) => {
      const vid = tile.getAttribute("data-variant-id");
      const btn = $(SELECTOR.addBtn, tile);
      if (!vid || !btn) return;

      const isAvailable = btn.tagName === "BUTTON" && !btn.classList.contains("is-disabled");
      if (isAvailable && isVariantInCart(vid)) selectedIds.add(String(vid));
    });

    return state.cart.items.filter(
      (item) => selectedIds.has(String(item.variant_id)) && item.quantity > 0
    );
  }

  function getSelectedQty() {
    return getSelectedBinderItems().reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  function getSelectedOriginalTotal() {
    return getSelectedBinderItems().reduce((sum, item) => {
      const line =
        item.original_line_price ??
        ((item.original_price ?? item.final_price ?? 0) * (item.quantity || 1));
      return sum + line;
    }, 0);
  }

  function getSelectedFinalTotal() {
    return getSelectedBinderItems().reduce((sum, item) => {
      const line =
        item.final_line_price ??
        ((item.final_price ?? item.price ?? 0) * (item.quantity || 1));
      return sum + line;
    }, 0);
  }

  function getSelectedSavings() {
    return Math.max(0, getSelectedOriginalTotal() - getSelectedFinalTotal());
  }

  function hasFreeGiftLine() {
    return getSelectedBinderItems().some((item) => {
      const line =
        item.final_line_price ??
        ((item.final_price ?? item.price ?? 0) * (item.quantity || 1));
      return line === 0;
    });
  }

  /* ──────────────────────────────────────────────────────────
     PROMO TEXT
     ────────────────────────────────────────────────────────── */

function getActivePromo() {
  const selectedTiles = $$(".binder-card.is-selected");

  for (const tile of selectedTiles) {
    const group = tile.closest(".binder-group");
    if (!group) continue;

    const label = group.dataset.productPromoLabel;
    if (!label || !label.trim()) continue;

    return {
      type:       group.dataset.productPromoType || "",
      label:      label.trim(),
      threshold:  group.dataset.productPromoThreshold || 0,
      buyQty:     group.dataset.productPromoBuyQty || 0,
      getQty:     group.dataset.productPromoGetQty || 0,
      percentOff: group.dataset.productPromoPercentOff || 0,
      amountOff:  group.dataset.productPromoAmountOff || 0,
      source:     "product"
    };
  }

  return {
    ...getPromo(),
    source: "collection"
  };
}

  function updatePromoText() {
    const el = $(SELECTOR.promoText);
    if (!el) return;

    const promo        = getActivePromo();
    const type         = String(promo.type || "").trim();
    const qty          = getSelectedQty();
    const originalTotal = getSelectedOriginalTotal();
    const savings      = getSelectedSavings();

    // Write into the inner text span so we never destroy the dot separator
    const textSpan = el.querySelector(".binder-bottom-bar__promo-text");
    const target   = textSpan || el;

    const hide = () => {
      el.classList.add("is-hidden");
      el.setAttribute("aria-hidden", "true");
      el.removeAttribute("data-state");
    };

    const show = (message, dataState) => {
      target.textContent = message;
      el.setAttribute("data-state", dataState);
      el.classList.remove("is-hidden");
      el.removeAttribute("aria-hidden");
    };

if (qty === 0) return hide();

    // ── Free Shipping nudge (always on, no metafield needed) ──
    if (!type) {
      if (qty >= 2) {
        show("Free shipping unlocked", "applied");
      } else {
        show("Add 1 more card for free shipping", "progress");
      }
      return;
    }

    // ── Percentage discount ──────────────────────────────────
    // Accepts both "percentage" (metafield value) and "product_percent" (legacy)
if (type === "percentage" || type === "product_percent") {
  const percent = Number(promo.percentOff || 0);
  const label = promo.label || `${percent}% off`;
  const isMobile = window.innerWidth < 640;

  if (savings > 0) {
    show(isMobile ? label : `${label} — saving ${moneyFromCartJS(savings)}`, "applied");
  } else {
    show(label, "available");
  }
  return;
}

    // ── Buy X Get Y ──────────────────────────────────────────
if (type === "bxgy") {
  const buyQty      = Number(promo.buyQty || 0);
  const progressWrap = document.querySelector("[data-binder-progress]");
  const progressFill = document.querySelector("[data-binder-progress-fill]");
  const isMobile    = window.innerWidth < 640;

  const setProgress = (pct, visible) => {
    if (progressFill) progressFill.style.width = `${Math.min(100, Math.max(0, pct))}%`;
    if (progressWrap) progressWrap.style.display = (!isMobile && visible) ? "block" : "none";
  };

  if (hasFreeGiftLine() || savings > 0) {
    setProgress(100, false);
    show(promo.label || "Offer applied", "applied");
  } else if (buyQty > 0 && qty < buyQty) {
    const needed = buyQty - qty;
    setProgress((qty / buyQty) * 100, true);
    show(`Add ${needed} more card${needed === 1 ? "" : "s"} to get 1 free`, "progress");
  } else if (qty >= buyQty) {
    setProgress(100, true);
    show("Choose your free card below", "available");
  } else {
    setProgress(0, false);
    hide();
  }
  return;
}
    // ── Spend & Get ──────────────────────────────────────────
    if (type === "spend_get") {
      const thresholdPence = normaliseMoneyToPence(promo.threshold);
      const amountOffPence = normaliseMoneyToPence(promo.amountOff);

      if (savings > 0) {
        show(promo.label || `${moneyFromCartJS(amountOffPence)} off applied`, "applied");
      } else if (thresholdPence > 0 && originalTotal < thresholdPence) {
        const remaining = thresholdPence - originalTotal;
        show(
          `${moneyFromCartJS(remaining)} away from ${promo.label || `${moneyFromCartJS(amountOffPence)} off`}`,
          "progress"
        );
      } else {
        show(promo.label || `${moneyFromCartJS(amountOffPence)} off unlocked`, "available");
      }
      return;
    }

// ── Free Shipping nudge (explicit type) ──────────────────
    if (type === "free_shipping") {
      if (qty >= 2) {
        show("Free shipping unlocked! 🎉", "applied");
      } else {
        show("Add 1 more card for free shipping 🚚", "progress");
      }
      return;
    }

    hide();
  }

  /* ──────────────────────────────────────────────────────────
     COUNTDOWN
     ────────────────────────────────────────────────────────── */

  function getNextFriday7pmMs() {
    const now   = new Date();
    const ukStr = now.toLocaleString("en-GB", { timeZone: "Europe/London" });
    const parts = ukStr.split(/[\s,\/\:]+/);

    const ukNow = new Date(
      parseInt(parts[2], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[0], 10),
      parseInt(parts[3], 10),
      parseInt(parts[4], 10),
      parseInt(parts[5] || "0", 10)
    );

    const target = new Date(ukNow);
    target.setHours(19, 0, 0, 0);

    const day = ukNow.getDay();
    let daysUntil = (5 - day + 7) % 7;
    if (daysUntil === 0 && ukNow >= target) daysUntil = 7;

    target.setDate(target.getDate() + daysUntil);
    return Math.max(0, target.getTime() - ukNow.getTime());
  }

  function pad(n) {
    return n < 10 ? `0${n}` : String(n);
  }

  function updateClosesCountdown() {
    const el = $(SELECTOR.counterCloses);
    if (!el) return;

    let ms = getNextFriday7pmMs();
    let s  = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400); s %= 86400;
    const h = Math.floor(s / 3600);  s %= 3600;
    const m = Math.floor(s / 60);

    el.textContent = `${d}D ${pad(h)}H ${pad(m)}M`;
  }

  function startClosesCountdown() {
    updateClosesCountdown();
    setInterval(updateClosesCountdown, 60000);
  }

  /* ──────────────────────────────────────────────────────────
     TOTALS
     ────────────────────────────────────────────────────────── */

  function updateBinderTotal() {
    const savings     = getSelectedSavings();
    const finalTotal  = getSelectedFinalTotal();
    const origTotal   = getSelectedOriginalTotal();

    // Final (discounted) price
    const finalEl = $(SELECTOR.counterTotalPrice);
    if (finalEl) finalEl.textContent = moneyFromCartJS(finalTotal);

    // Strikethrough original price — only shown when there's an actual saving
    const origEl = $(SELECTOR.counterOrigPrice);
    if (origEl) {
      if (savings > 0) {
        origEl.textContent  = moneyFromCartJS(origTotal);
        origEl.style.display = "";
      } else {
        origEl.textContent  = "";
        origEl.style.display = "none";
      }
    }
  }

  /* ──────────────────────────────────────────────────────────
     GROUP TABS
     ────────────────────────────────────────────────────────── */

  function updateGroupTabs() {
    $$(SELECTOR.groupTab).forEach((group) => {
      const tiles        = $$(SELECTOR.tile, group);
      const selectAllBtn = $(SELECTOR.groupSelectAll, group);
      const statusEl     = $(SELECTOR.groupStatus, group);
      const remainingEl  = $(SELECTOR.groupRemaining, group);
      const priceEl      = $(SELECTOR.groupPrice, group);

      let totalCount     = 0;
      let availableCount = 0;
      let selectedCount  = 0;
      let totalPrice     = 0;
      let lowestPrice    = Infinity;

      tiles.forEach((tile) => {
        const vid = tile.getAttribute("data-variant-id");
        const btn = $(SELECTOR.addBtn, tile);
        if (!vid || !btn || btn.tagName !== "BUTTON") return;

        totalCount++;

        if (!btn.classList.contains("is-disabled")) {
          availableCount++;
          const rawPrice = tile.getAttribute("data-price");
          if (rawPrice) {
            const num = parseFloat(rawPrice.replace(/[^0-9.]/g, ""));
            if (!Number.isNaN(num) && num < lowestPrice) lowestPrice = num;
          }
        }

        if (isVariantInCart(vid) && state.cart?.items) {
          selectedCount++;
          const item = state.cart.items.find(
            (i) => String(i.variant_id) === String(vid) && i.quantity > 0
          );
          if (item) {
            totalPrice += item.final_line_price ?? (item.final_price * item.quantity) ?? 0;
          }
        }
      });

      const remaining  = Math.max(0, availableCount - selectedCount);
      const allInStock = availableCount === totalCount && totalCount > 0;
      const allSelected = selectedCount === availableCount && availableCount > 0;
      const allSoldOut  = availableCount === 0;

      if (remainingEl) remainingEl.textContent = String(remaining);

      if (priceEl) {
        if (allSelected && totalPrice > 0) {
          priceEl.textContent = moneyFromCartJS(totalPrice);
          priceEl.setAttribute("data-mode", "total");
        } else if (lowestPrice !== Infinity) {
          priceEl.textContent = `from £${lowestPrice.toFixed(2)}`;
          priceEl.setAttribute("data-mode", "from");
        } else {
          priceEl.textContent = "sold out";
          priceEl.setAttribute("data-mode", "soldout");
        }
      }

      if (selectAllBtn) {
        if (allSoldOut) {
          selectAllBtn.style.display = "none";
        } else if (allInStock && !allSelected) {
          selectAllBtn.style.display = "";
          selectAllBtn.textContent   = "Select all →";
          selectAllBtn.classList.remove("is-selected");
          selectAllBtn.disabled = false;
        } else if (allSelected) {
          selectAllBtn.style.display = "";
          selectAllBtn.textContent   = "All selected ✓";
          selectAllBtn.classList.add("is-selected");
          selectAllBtn.disabled = false;
        } else {
          selectAllBtn.style.display = "none";
        }
      }

      if (statusEl) {
        if (allSoldOut) {
          statusEl.textContent    = "Sold out";
          statusEl.style.display  = "";
        } else if (!allInStock && !allSelected) {
          statusEl.textContent    = "Some claimed";
          statusEl.style.display  = "";
        } else {
          statusEl.style.display  = "none";
        }
      }
    });
  }

  /* ──────────────────────────────────────────────────────────
     GROUP SELECT ALL
     ────────────────────────────────────────────────────────── */

  async function handleSelectAll(btn) {
    if (state.toggling) return;

    const group = btn.closest(SELECTOR.groupTab);
    if (!group) return;

    const tiles = $$(SELECTOR.tile, group);

    const availableTiles = tiles.filter((tile) => {
      const vid    = tile.getAttribute("data-variant-id");
      const addBtn = $(SELECTOR.addBtn, tile);
      return vid && addBtn && addBtn.tagName === "BUTTON" && !addBtn.classList.contains("is-disabled");
    });

    const allSelected = availableTiles.every((tile) =>
      isVariantInCart(tile.getAttribute("data-variant-id"))
    );

    if (allSelected) {
      // Deselect all
      const updates = {};
      availableTiles.forEach((tile) => { updates[tile.getAttribute("data-variant-id")] = 0; });
      if (!Object.keys(updates).length) return;

      state.toggling = true;
      btn.disabled   = true;
      updateHeaderCounters();

      try {
        state.cart = await apiUpdate(updates);
        renderFromCart();
        dispatchCartRefreshEvents();
      } catch (e) {
        console.error("[Binder] group deselect all failed:", e);
        await syncCartAndRender();
      } finally {
        state.toggling = false;
        btn.disabled   = false;
        updateHeaderCounters();
      }

    } else {
      // Select missing
      const toAdd = availableTiles.filter(
        (tile) => !isVariantInCart(tile.getAttribute("data-variant-id"))
      );
      if (!toAdd.length) return;

      state.toggling = true;
      btn.disabled   = true;
      updateHeaderCounters();

      // Optimistic UI
      toAdd.forEach((tile) => {
        tile.classList.add("is-selected");
        const addBtn = $(SELECTOR.addBtn, tile);
        if (addBtn) {
          const label = addBtn.querySelector(".binder-add-label");
          if (label) label.textContent = "Selected";
          addBtn.setAttribute("aria-pressed", "true");
        }
      });

      try {
        const items = toAdd.map((tile) => ({
          id:       Number(tile.getAttribute("data-variant-id")),
          quantity: 1
        }));
        await fetch("/cart/add.js", {
          method:  "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ items })
        });
        state.cart = await fetchCart();
        renderFromCart();
        dispatchCartRefreshEvents();
      } catch (e) {
        console.error("[Binder] group select all failed:", e);
        await syncCartAndRender();
      } finally {
        state.toggling = false;
        btn.disabled   = false;
        updateHeaderCounters();
      }
    }
  }

  /* ──────────────────────────────────────────────────────────
     HEADER / BOTTOM BAR
     ────────────────────────────────────────────────────────── */

  function updateHeaderCounters() {
    const clearBtns     = document.querySelectorAll(SELECTOR.clearAllBtn);
    const checkoutLinks = document.querySelectorAll(SELECTOR.checkoutLink);
    const selectedEls   = document.querySelectorAll(SELECTOR.counterSelected);
    const bottomBar     = $(SELECTOR.bottomBar);
    const tiles         = $$(SELECTOR.tile);

    let selectedCount = 0;

    for (const tile of tiles) {
      const vid = tile.getAttribute("data-variant-id");
      const btn = $(SELECTOR.addBtn, tile);
      if (!vid || !btn) continue;

      const isAvailable = btn.tagName === "BUTTON" && !btn.classList.contains("is-disabled");
      if (isAvailable && isVariantInCart(vid)) selectedCount++;
    }

    const noneSelected = selectedCount === 0 || state.toggling;

    selectedEls.forEach((el) => { el.textContent = String(selectedCount); });

    clearBtns.forEach((btn) => { btn.disabled = noneSelected; });

    checkoutLinks.forEach((link) => {
      link.classList.toggle("is-disabled", noneSelected);
      link.setAttribute("aria-disabled", noneSelected ? "true" : "false");
      if (noneSelected) {
        link.setAttribute("tabindex", "-1");
      } else {
        link.removeAttribute("tabindex");
      }
    });

    if (bottomBar) {
      bottomBar.classList.toggle("is-visible", selectedCount > 0);
      document.body.classList.toggle("binder-bar-active", selectedCount > 0);
    }

    updateBinderTotal();
    updatePromoText();
    updateGroupTabs();
  }

  /* ──────────────────────────────────────────────────────────
     TRAY
     ────────────────────────────────────────────────────────── */

  function buildTrayItemHTML(item) {
    const variantId = item.variant_id;
    const img       = item.image ? item.image.replace(/(\?.*)?$/, "") : "";
    const title     = item.product_title || item.title || "Item";
    const variant   =
      item.variant_title && item.variant_title !== "Default Title"
        ? item.variant_title
        : "";
    const price = moneyFromCartJS(item.final_price ?? item.price);

    return `
      <li class="binder-tray__item" data-variant-id="${variantId}">
        ${img ? `<img class="binder-tray__thumb" src="${img}" alt="">` : ""}
        <div class="binder-tray__info">
          <div class="binder-tray__item-title">${escapeHtml(title)}</div>
          ${variant ? `<div class="binder-tray__item-variant">${escapeHtml(variant)}</div>` : ""}
          <div class="binder-tray__item-price">${price}</div>
        </div>
        <button class="binder-tray__remove" type="button" aria-label="Remove">✕</button>
      </li>
    `;
  }

  function renderTray() {
    const tray = $(SELECTOR.tray);
    if (!tray) return;

    const trayList     = $(SELECTOR.trayList, tray);
    const trayCount    = $(SELECTOR.trayCount, tray);
    const traySubtotal = $(SELECTOR.traySubtotal, tray);
    const items        = (state.cart?.items || []).filter((item) => item.quantity > 0);

    if (trayList)     trayList.innerHTML = items.map(buildTrayItemHTML).join("");
    if (trayCount)    trayCount.textContent = String(items.reduce((a, item) => a + item.quantity, 0));
    if (traySubtotal) traySubtotal.textContent = moneyFromCartJS(state.cart?.total_price || 0);

    tray.classList.toggle("is-visible", items.length > 0);
  }

  /* ──────────────────────────────────────────────────────────
     RENDER / SYNC
     ────────────────────────────────────────────────────────── */

  function renderFromCart() {
    for (const tile of $$(SELECTOR.tile)) {
      const vid      = tile.getAttribute("data-variant-id");
      const selected = isVariantInCart(vid);

      tile.classList.toggle("is-selected", selected);

      const btn = $(SELECTOR.addBtn, tile);
      if (btn) {
        const label = $(".binder-add-label", btn);
        if (label) label.textContent = selected ? "Selected" : "Select";
        btn.setAttribute("aria-pressed", selected ? "true" : "false");
      }
    }

    renderTray();
    updateHeaderCounters();
  }

  async function syncCartAndRender() {
    if (state.syncing || state.toggling) return;

    state.syncing = true;
    try {
      state.cart = await fetchCart();
      renderFromCart();
      dispatchCartRefreshEvents();
    } catch (e) {
      console.error("[Binder] sync error:", e);
    } finally {
      state.syncing = false;
    }
  }

  /* ──────────────────────────────────────────────────────────
     SELECT / REMOVE
     ────────────────────────────────────────────────────────── */

  async function handleSelectClick(btn) {
    if (state.toggling || btn.disabled) return;

    const variantId = btn.getAttribute("data-variant-id");
    if (!variantId) return;

    state.toggling = true;
    btn.disabled   = true;

    const already = isVariantInCart(variantId);
    const tile    = btn.closest(SELECTOR.tile);

    if (tile && !already) tile.classList.add("is-loading");
    if (tile) tile.classList.toggle("is-selected", !already);

    const label = btn.querySelector(".binder-add-label");
    if (label) label.textContent = already ? "Select" : "Selected";
    btn.setAttribute("aria-pressed", already ? "false" : "true");

    updateHeaderCounters();

    try {
      if (already) {
        state.cart = await apiUpdate({ [String(variantId)]: 0 });
      } else {
        await addToCart(variantId);
        state.cart = await fetchCart();
      }
      renderFromCart();
      dispatchCartRefreshEvents();
    } catch (e) {
      console.error("[Binder] select toggle failed:", e);
      await syncCartAndRender();
    } finally {
      if (tile) tile.classList.remove("is-loading");
      btn.disabled   = false;
      state.toggling = false;
      updateHeaderCounters();
    }
  }

  async function handleTrayRemoveClick(removeBtn) {
    const li        = removeBtn.closest("[data-variant-id]");
    const variantId = li?.getAttribute("data-variant-id");
    if (!variantId || state.toggling) return;

    state.toggling     = true;
    removeBtn.disabled = true;
    updateHeaderCounters();

    try {
      state.cart = await apiUpdate({ [String(variantId)]: 0 });
      renderFromCart();
      dispatchCartRefreshEvents();
    } catch (e) {
      console.error("[Binder] tray remove failed:", e);
      await syncCartAndRender();
    } finally {
      removeBtn.disabled = false;
      state.toggling     = false;
      updateHeaderCounters();
    }
  }

  async function handleClearAll(btn) {
    if (state.toggling) return;

    const updates = {};
    for (const tile of $$(SELECTOR.tile)) {
      const vid = tile.getAttribute("data-variant-id");
      const add = $(SELECTOR.addBtn, tile);
      if (!vid || !add || add.tagName !== "BUTTON") continue;
      if (!isVariantInCart(vid)) continue;
      updates[String(vid)] = 0;
    }
    if (!Object.keys(updates).length) return;

    state.toggling = true;
    btn.disabled   = true;
    updateHeaderCounters();

    try {
      state.cart = await apiUpdate(updates);
      renderFromCart();
      dispatchCartRefreshEvents();
    } catch (e) {
      console.error("[Binder] clear all failed:", e);
      await syncCartAndRender();
    } finally {
      state.toggling = false;
      btn.disabled   = false;
      updateHeaderCounters();
    }
  }

  /* ──────────────────────────────────────────────────────────
     EVENTS
     ────────────────────────────────────────────────────────── */

  function bindEvents() {

    document.addEventListener("click", (e) => {
      const btn = e.target.closest(SELECTOR.clearAllBtn);
      if (!btn) return;
      e.preventDefault();
      handleClearAll(btn);
    });

    document.addEventListener("click", (e) => {
      const btn = e.target.closest(SELECTOR.groupSelectAll);
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      handleSelectAll(btn);
    });

    document.addEventListener("click", (e) => {
      const link = e.target.closest(SELECTOR.checkoutLink);
      if (!link) return;
      if (link.classList.contains("is-disabled")) e.preventDefault();
    });

    document.addEventListener(
      "pointerdown",
      (e) => {
        const btn = e.target.closest(SELECTOR.addBtn);
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled || state.toggling) return;
        handleSelectClick(btn);
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      "click",
      (e) => {
        const isMobile = window.innerWidth < 768;
        const hit = e.target.closest(
          isMobile
            ? ".card-image-link, .card-image"
            : ".card-image-link, .card-image, .card-meta, .card-price"
        );
        if (!hit) return;
        if (e.target.closest("a, button")) return;
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button === 1) return;

        const tile = hit.closest(SELECTOR.tile);
        if (!tile) return;

        const btn = $(SELECTOR.addBtn, tile);
        if (!btn || btn.tagName !== "BUTTON") return;

        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled || state.toggling) return;
        handleSelectClick(btn);
      },
      { capture: true }
    );

    const tray = $(SELECTOR.tray);
    if (tray) {
      const header = $(SELECTOR.trayToggle, tray);
      const body   = $(SELECTOR.trayBody, tray);

      if (header && body) {
        header.addEventListener("click", (e) => {
          if (e.target.closest(".binder-tray__remove")) return;
          body.classList.toggle("is-open");
        });
      }

      tray.addEventListener("click", (e) => {
        const removeBtn = e.target.closest(".binder-tray__remove");
        if (!removeBtn) return;
        e.preventDefault();
        handleTrayRemoveClick(removeBtn);
      });
    }

    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) syncCartAndRender();
    });

    window.addEventListener("focus",    () => syncCartAndRender());
    window.addEventListener("pageshow", () => syncCartAndRender());

    document.addEventListener("click", (e) => {
      const backTop = e.target.closest("[data-binder-backtop]");
      if (!backTop) return;
      e.preventDefault();

      const target = document.querySelector(".binder-container");
      if (!target) return;

      const header = document.querySelector("header, .header, .shopify-section-header");
      const offset = header ? header.offsetHeight + 10 : 10;
      const top    = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({ top, behavior: "smooth" });
    });
  }

  function listenForExternalCartChanges() {
    const names = [
      "cart:refresh", "cart:updated", "cart:update",
      "theme:cart:refresh", "theme:cart:updated",
      "ajaxCart:refresh", "ajaxCart:updated",
      "cart:change", "cart:render"
    ];

    const handler = (e) => {
      if (e?.detail?.source === "binder") return;
      if (state.toggling) return;
      syncCartAndRender();
    };

    names.forEach((name) => {
      document.addEventListener(name, handler);
      document.documentElement.addEventListener(name, handler);
      window.addEventListener(name, handler);
    });
  }

  /* ──────────────────────────────────────────────────────────
     STOCK POLLING
     ────────────────────────────────────────────────────────── */

  function buildSleeve() {
    const wrap  = document.createElement("div");
    wrap.className = "binder-empty-sleeve";
    wrap.setAttribute("aria-hidden", "true");

    const label = document.createElement("span");
    label.className = "binder-empty-label binder-empty-label--single";
    label.setAttribute("data-claimed-label", "");

    const top = document.createElement("span");
    top.className   = "binder-empty-label__top";
    top.textContent = "CLAIMED";

    label.appendChild(top);
    wrap.appendChild(label);
    return wrap;
  }

  function markCardAsSoldOut(variantId) {
    const card = document.querySelector(`.binder-card[data-variant-id="${variantId}"]`);
    if (!card) return;

    claimTimes[variantId] = new Date();

    const img = card.querySelector(".card-image img");
    if (img) img.replaceWith(buildSleeve());

    const price = card.querySelector(".card-price");
    if (price) price.innerHTML = '<span class="binder-soldout">Sold out</span>';

    const btn = card.querySelector(".binder-add[data-variant-id]");
    if (btn && btn.tagName === "BUTTON") {
      const ghost = document.createElement("div");
      ghost.className = "binder-add binder-add--check is-disabled";
      ghost.setAttribute("data-variant-id", variantId);
      btn.replaceWith(ghost);
    }

    card.classList.add("is-empty");
    card.classList.remove("is-selected");

    updateGroupTabs();
    updateHeaderCounters();
  }

  function updateClaimedLabels() {
    const now = new Date();

    document.querySelectorAll(".binder-card[data-variant-id]").forEach((card) => {
      const variantId = card.dataset.variantId;

      let claimedAt = claimTimes[variantId];
      if (!claimedAt) {
        const ts = card.dataset.claimedAt;
        if (!ts || ts === "0") return;
        claimedAt = new Date(parseInt(ts, 10) * 1000);
        claimTimes[variantId] = claimedAt;
      }

      const label = card.querySelector("[data-claimed-label]");
      if (!label) return;

      const diffMs    = now - claimedAt;
      const diffMins  = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays  = Math.floor(diffHours / 24);

      let isDouble   = false;
      let bottomText = "";

      if (diffMins < 1) {
        isDouble = false;
      } else if (diffMins < 60) {
        isDouble    = true;
        bottomText  = `${diffMins} ${diffMins === 1 ? "MIN" : "MINS"} AGO`;
      } else if (diffHours < 24) {
        isDouble    = true;
        bottomText  = `${diffHours} ${diffHours === 1 ? "HOUR" : "HOURS"} AGO`;
      } else {
        isDouble    = true;
        bottomText  = `${diffDays} ${diffDays === 1 ? "DAY" : "DAYS"} AGO`;
      }

      label.className = `binder-empty-label ${isDouble ? "binder-empty-label--double" : "binder-empty-label--single"}`;

      let topEl = label.querySelector(".binder-empty-label__top");
      if (!topEl) {
        topEl           = document.createElement("span");
        topEl.className = "binder-empty-label__top";
        label.appendChild(topEl);
      }
      topEl.textContent = "CLAIMED";

      let bottomEl = label.querySelector(".binder-empty-label__bottom");
      if (isDouble) {
        if (!bottomEl) {
          bottomEl           = document.createElement("span");
          bottomEl.className = "binder-empty-label__bottom";
          label.appendChild(bottomEl);
        }
        bottomEl.textContent = bottomText;
      } else if (bottomEl) {
        bottomEl.remove();
      }
    });
  }

  async function pollAllProducts() {
    const groups = document.querySelectorAll(".binder-group[data-product-handle]");

    for (const group of groups) {
      const handle = group.dataset.productHandle;
      if (!handle) continue;

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) continue;

        const product = await res.json();

        product.variants.forEach((variant) => {
          const id       = String(variant.id);
          const current  = variant.inventory_quantity;
          const previous = stockState[id];

          if (previous === undefined) { stockState[id] = current; return; }
          if (current === previous) return;
          if (current <= 0 && previous > 0) markCardAsSoldOut(id);

          stockState[id] = current;
        });
      } catch (err) {
        console.warn(`[Binder] poll failed for ${handle}, skipping:`, err);
      }
    }
  }

  function initPolling() {
    document.querySelectorAll(".binder-card[data-claimed-at]").forEach((card) => {
      const ts = card.dataset.claimedAt;
      if (ts && ts !== "0") {
        claimTimes[card.dataset.variantId] = new Date(parseInt(ts, 10) * 1000);
      }
    });

    pollAllProducts();
    setInterval(pollAllProducts, 15000);

    updateClaimedLabels();
    setInterval(updateClaimedLabels, 30000);
  }

  /* ──────────────────────────────────────────────────────────
     INIT
     ────────────────────────────────────────────────────────── */

  function init() {
    if (!$(SELECTOR.tile)) return;

    bindEvents();
    listenForExternalCartChanges();
    syncCartAndRender();
    startClosesCountdown();
    initPolling();
  }

  document.addEventListener("DOMContentLoaded", init);
})();